import { Children, Fragment, isValidElement, ReactElement, ReactNode } from "react";

import { cn } from "./cn";
import { EmptyRow } from "./empty-state";
import { Table, TableBody, TableFooter, TableHead, TableHeader, TableRow } from "./table";

export interface ListTableColumn {
  // 머리 칸 글자 — 숨김 머리는 <span className="sr-only">…</span> 를 넘긴다.
  label: ReactNode;
  // 금액·개수 열은 right.
  align?: "left" | "right";
  // 공사명·현장처럼 긴 글이 접혀도 되는 열 — 기본은 한 줄(whitespace-nowrap).
  wrap?: boolean;
  // 열 폭(min-w-32·w-12 등) — 색은 넣지 않는다.
  className?: string;
}

interface Props {
  columns: ListTableColumn[];
  // TableRow 들 — 하나도 없으면 empty 를 EmptyRow 로 그린다.
  children?: ReactNode;
  empty?: ReactNode;
  // default 는 배경 없는 머리, log 는 발송·이력 기록 표의 회색 작은 머리(TableHeader variant).
  headerVariant?: "default" | "log";
  // 합계 줄 등 — 있으면 TableFooter(tfoot) 안에 그린다(TableRow 들을 넘긴다).
  footer?: ReactNode;
  // 감싸개(테두리 상자)에 붙는다 — hidden md:block 처럼 보임 전환용.
  className?: string;
  // table 요소에 붙는다 — min-w-[40rem] 처럼 가로 스크롤 기준 폭.
  tableClassName?: string;
  testId?: string;
}

// 빈 Fragment(<></>)·null·false 는 행이 아니다 — Fragment 를 펼쳐 실제 행을 센다.
const hasRows = (children: ReactNode): boolean =>
  Children.toArray(children).some((child) =>
    isValidElement(child) && child.type === Fragment ? hasRows((child as ReactElement<{ children?: ReactNode }>).props.children) : true
  );

/** 목록 화면 표(columns 한 칸 = { label, align?: "left" | "right", wrap?, className? }) — 테두리 상자 + 머리(h-10 px-3 · 기본은 배경 없음, headerVariant="log" 는 회색 작은 머리) + 합계 줄은 footer(TableFooter) + 행이 없으면 empty 를 EmptyRow 로, 넘치면 상자 안에서만 가로 스크롤(375px 에서도 페이지는 안 밀린다) · 행·칸은 TableRow·TableCell(px-3 py-2)로 직접 그린다 · 첫 열은 식별자 링크(행 `relative` + 링크 `after:absolute after:inset-0` 로 행 전체 클릭) · 금액은 Money + align:"right" · 날짜는 MM-DD text-muted-foreground · 빈 글은 「견적이 없습니다.」, 필터가 걸렸으면 「조건에 맞는 견적이 없습니다.」 · 폰 카드 목록과 갈아끼울 땐 ResponsiveList 의 table 로 넘긴다. */
const ListTable = ({ columns, children, empty, headerVariant = "default", footer, className, tableClassName, testId }: Props) => {
  const isEmpty = !hasRows(children);
  return (
    <div className={cn("min-w-0 overflow-hidden rounded-lg border border-border", className)} data-testid={testId}>
      <Table className={tableClassName}>
        <TableHeader variant={headerVariant}>
          <TableRow>
            {columns.map((col, i) => (
              <TableHead
                key={i}
                className={cn("h-10 px-3", !col.wrap && "whitespace-nowrap", col.align === "right" && "text-right", col.className)}>
                {col.label}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {isEmpty && empty !== undefined ? <EmptyRow colSpan={columns.length}>{empty}</EmptyRow> : children}
        </TableBody>
        {footer !== undefined && footer !== null ? <TableFooter>{footer}</TableFooter> : null}
      </Table>
    </div>
  );
};

export default ListTable;
